import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Maps each authorised app email to the Sofi user that must appear as
// "Agente de ventas" when a trip is pushed. Unmapped → Sofi falls back to
// the shared login account (the warning agents see in the builder).
export function SofiAgentMap({ allowed }) {
  const [map, setMap] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [agents, setAgents] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [saving, setSaving] = useState(null);

  const loadMap = async () => {
    const { data } = await api.get("/admin/sofi-agent-map");
    setMap(data.map); setDefaults(data.defaults);
  };
  useEffect(() => { loadMap().catch(() => setMap({})); }, []);

  const loadAgents = async (refresh = false) => {
    setLoadingAgents(true);
    try {
      const { data } = await api.get(`/admin/sofi-agents${refresh ? "?refresh=1" : ""}`);
      setAgents(data.agents);
      if (!data.cached) toast.success(`${data.agents.length} agentes leídos de Sofi`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo leer Sofi"); }
    finally { setLoadingAgents(false); }
  };

  const assign = async (email, value) => {
    setSaving(email);
    try {
      await api.put("/admin/sofi-agent-map", { email, sofi_agent_id: value ? Number(value) : null });
      await loadMap();
      toast.success(value ? `${email} → Sofi #${value}` : `Mapeo de ${email} eliminado`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(null); }
  };

  if (!map) return null;
  const nameOf = (id) => agents?.find((a) => a.id === id)?.name;
  const missing = allowed.filter((a) => !map[a.email]).length;

  return (
    <div className="mt-8" data-testid="sofi-agent-map">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="smallcaps">Agente en Sofi</div>
          <p className="text-[12px] text-clay-700 mt-1">
            Quién aparece como <em>agente de ventas</em> en Sofi al enviar un viaje. Sin mapeo, Sofi pone la cuenta compartida.
            {missing > 0 && <span className="text-terracotta font-medium"> · {missing} sin mapear</span>}
          </p>
        </div>
        <button onClick={() => loadAgents(!!agents)} disabled={loadingAgents} data-testid="sofi-agents-load-btn"
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-clay-300 text-[11px] tracking-wider uppercase hover:border-clay-900 disabled:opacity-50">
          {loadingAgents ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} {agents ? "Releer agentes de Sofi" : "Cargar agentes de Sofi"}
        </button>
      </div>
      <div className="border border-clay-300 bg-white">
        {allowed.map((a) => {
          const id = map[a.email];
          return (
            <div key={a.email} className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-2.5 border-t first:border-t-0 border-clay-300 text-sm" data-testid={`sofi-map-${a.email}`}>
              <div className="min-w-0">
                <div className="font-semibold truncate">{a.email}</div>
                <div className="text-[11px] text-clay-600">
                  {id ? <>Sofi #{id}{nameOf(id) ? ` · ${nameOf(id)}` : ""}{defaults[a.email] === id && !agents ? "" : ""}</>
                      : <span className="inline-flex items-center gap-1 text-terracotta"><AlertTriangle size={11}/> sin mapear</span>}
                </div>
              </div>
              {agents ? (
                <select value={id || ""} onChange={(e) => assign(a.email, e.target.value)} disabled={saving === a.email}
                  data-testid={`sofi-map-select-${a.email}`} className="bg-white border border-clay-300 px-2 py-1.5 text-[12px] max-w-[220px]">
                  <option value="">— sin mapear —</option>
                  {agents.map((ag) => <option key={ag.id} value={ag.id}>{ag.name} (#{ag.id})</option>)}
                </select>
              ) : (
                <input type="number" placeholder="ID Sofi" defaultValue={id || ""} disabled={saving === a.email}
                  onBlur={(e) => { const v = e.target.value; if (String(id || "") !== v) assign(a.email, v); }}
                  data-testid={`sofi-map-input-${a.email}`} className="bg-white border border-clay-300 px-2 py-1.5 text-[12px] w-24"/>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
